import type { IconType } from 'react-icons'
import { isBdmTeam } from '../shared/data/division'
import { canBookMeetingRooms, hasInterviewNavigation } from '../common/navigationRules'
import {
  HiHome,
  HiUser,
  HiCheckCircle,
  HiChartBar,
  HiReceiptPercent,
  HiCalendarDays,
  HiDocumentText,
  HiBookOpen,
  HiShieldCheck,
  HiRectangleGroup,
  HiUserGroup,
  HiTicket,
  HiBanknotes,
  HiWrenchScrewdriver,
  HiMegaphone,
  HiLightBulb,
  HiStar,
  HiBuildingOffice2,
  HiClock,
  HiArrowsRightLeft,
  HiCog6Tooth,
  HiDocumentDuplicate,
  HiArchiveBox,
  HiQuestionMarkCircle,
  HiInboxArrowDown,
  HiDevicePhoneMobile,
  HiMapPin,
  HiHeart,
} from 'react-icons/hi2'
import { FaCoffee } from 'react-icons/fa'

export type PermissionContext = {
  userPermissions: any
  userEmail?: string
  documentsVisible: boolean
  billsAccess: boolean
  wellbeingAllowanceAccess: boolean
  duoServicesAccess: boolean
}

export type SearchEntry = {
  label: string
  path: string
  keywords?: string
  group: 'Quick Actions' | 'Pages' | 'Admin'
  icon?: IconType
  visible: (ctx: PermissionContext) => boolean
}

const isSystemDev = (ctx: PermissionContext) =>
  ctx.userPermissions?.Team__c === 'System Development'

const isBusinessDevelopmentManager = (ctx: PermissionContext) =>
  isBdmTeam(ctx.userPermissions?.Team__c)

const isDivisionLeader = (ctx: PermissionContext) => {
  const v = ctx.userPermissions?.Division_Leader__c
  return v === 'true' || v === true
}

// Keep Interviews discovery aligned with the sidebar and My Apps.
const hasInterviews = (ctx: PermissionContext) =>
  hasInterviewNavigation(ctx.userPermissions, ctx.userEmail)

const canAccessBills = (ctx: PermissionContext) => ctx.billsAccess

const isSydney = (ctx: PermissionContext) =>
  ctx.userPermissions?.Division_Location__c === 'Sydney' ||
  ctx.userPermissions?.Division_Location__c === 'Melbourne'

const always = () => true

export const SEARCH_REGISTRY: SearchEntry[] = [
  // Quick Actions
  {
    label: 'Submit a bill',
    path: '/bills/new',
    keywords: 'expense receipt new create',
    group: 'Quick Actions',
    icon: HiReceiptPercent,
    visible: canAccessBills,
  },
  {
    label: 'Request leave',
    path: '/leave/new',
    keywords: 'time off holiday vacation new',
    group: 'Quick Actions',
    icon: HiCalendarDays,
    visible: always,
  },
  {
    label: 'Book a meeting room',
    path: '/meeting-room',
    keywords: 'reserve room booking',
    group: 'Quick Actions',
    icon: HiBuildingOffice2,
    visible: (ctx) => canBookMeetingRooms(ctx.userPermissions),
  },
  {
    label: 'Order from the cafe',
    path: '/cafe',
    keywords: 'coffee food drink',
    group: 'Quick Actions',
    icon: FaCoffee,
    visible: isSydney,
  },
  {
    label: 'Raise an IT ticket',
    path: '/it-tickets',
    keywords: 'support help bug issue',
    group: 'Quick Actions',
    icon: HiWrenchScrewdriver,
    visible: always,
  },
  {
    label: 'Submit a marketing suggestion',
    path: '/marketing-suggestions',
    keywords: 'idea brand',
    group: 'Quick Actions',
    icon: HiMegaphone,
    visible: always,
  },
  {
    label: 'Submit an Einstein idea',
    path: '/einstein-submission',
    keywords: 'innovation idea',
    group: 'Quick Actions',
    icon: HiLightBulb,
    visible: always,
  },
  {
    label: 'Nominate a Superstar',
    path: '/superstar',
    keywords: 'award recognition',
    group: 'Quick Actions',
    icon: HiStar,
    visible: always,
  },

  // Pages
  {
    label: 'Home',
    path: '/',
    keywords: 'dashboard',
    group: 'Pages',
    icon: HiHome,
    visible: always,
  },
  {
    label: 'My Profile',
    path: '/my-profile',
    keywords:
      'account settings me bank details personal mobile home address emergency contact next of kin',
    group: 'Pages',
    icon: HiUser,
    visible: always,
  },
  {
    label: 'Bank Details',
    path: '/my-profile',
    keywords: 'bank account number bsb swift bic wise payroll salary pay',
    group: 'Pages',
    icon: HiBanknotes,
    visible: always,
  },
  {
    label: 'Personal Mobile',
    path: '/my-profile',
    keywords: 'phone number mobile contact',
    group: 'Pages',
    icon: HiDevicePhoneMobile,
    visible: always,
  },
  {
    label: 'Home Address',
    path: '/my-profile',
    keywords: 'address street city state postcode',
    group: 'Pages',
    icon: HiMapPin,
    visible: always,
  },
  {
    label: 'Emergency Contact',
    path: '/my-profile',
    keywords: 'next of kin nok emergency relationship',
    group: 'Pages',
    icon: HiUserGroup,
    visible: always,
  },
  {
    label: 'My Tasks',
    path: '/my-tasks',
    keywords: 'todo assigned action items',
    group: 'Pages',
    icon: HiCheckCircle,
    visible: always,
  },
  {
    label: 'My KPI',
    path: '/kpi',
    keywords: 'assessment review goals',
    group: 'Pages',
    icon: HiChartBar,
    visible: always,
  },
  {
    label: 'Bills',
    path: '/bills',
    keywords: 'expenses receipts',
    group: 'Pages',
    icon: HiReceiptPercent,
    visible: canAccessBills,
  },
  {
    label: 'Leave',
    path: '/leave',
    keywords: 'time off holiday',
    group: 'Pages',
    icon: HiCalendarDays,
    visible: always,
  },
  {
    label: 'Memos',
    path: '/memo',
    keywords: 'company announcements',
    group: 'Pages',
    icon: HiDocumentDuplicate,
    visible: always,
  },
  {
    label: 'Meeting Minutes',
    path: '/minutes',
    keywords: 'meetings notes',
    group: 'Pages',
    icon: HiBookOpen,
    visible: always,
  },
  {
    label: 'Documents',
    path: '/documents',
    keywords: 'sign contracts files',
    group: 'Pages',
    icon: HiDocumentText,
    visible: (c) => c.documentsVisible,
  },
  {
    label: 'Security Training',
    path: '/security-training',
    keywords: 'compliance modules',
    group: 'Pages',
    icon: HiShieldCheck,
    visible: always,
  },
  {
    label: 'Org Chart',
    path: '/org-chart',
    keywords: 'organisation hierarchy',
    group: 'Pages',
    icon: HiRectangleGroup,
    visible: always,
  },
  {
    label: 'Parramatta Seating Plan',
    path: '/parra-seating-plan',
    keywords: 'parra parramatta office desk seat seating floor plan move pitt street',
    group: 'Pages',
    icon: HiMapPin,
    visible: always,
  },
  {
    label: 'Interviews',
    path: '/interviews',
    keywords: 'hiring recruitment candidates resume feedback red flags interview',
    group: 'Pages',
    icon: HiUserGroup,
    visible: hasInterviews,
  },
  {
    label: 'My Team',
    path: '/my-team',
    keywords: 'colleagues team members',
    group: 'Pages',
    icon: HiUserGroup,
    visible: always,
  },
  {
    label: 'Company Directory',
    path: '/directory',
    keywords: 'people staff search',
    group: 'Pages',
    icon: HiUserGroup,
    visible: isDivisionLeader,
  },
  {
    label: 'Case Studies',
    path: '/case-studies',
    keywords: 'projects examples',
    group: 'Pages',
    icon: HiBookOpen,
    visible: always,
  },
  {
    label: 'Duo Cafe',
    path: '/cafe',
    keywords: 'coffee orders',
    group: 'Pages',
    icon: FaCoffee,
    visible: isSydney,
  },
  {
    label: 'Cafe Order History',
    path: '/cafe/history',
    keywords: 'past orders',
    group: 'Pages',
    icon: HiClock,
    visible: isSydney,
  },
  {
    label: 'IT Tickets',
    path: '/it-tickets',
    keywords: 'support help',
    group: 'Pages',
    icon: HiTicket,
    visible: always,
  },
  {
    label: 'Wise Setup',
    path: '/wise-setup',
    keywords: 'payments transfer',
    group: 'Pages',
    icon: HiBanknotes,
    visible: always,
  },
  {
    label: 'BNI',
    path: '/bni',
    keywords: 'networking',
    group: 'Pages',
    icon: HiUserGroup,
    visible: isBusinessDevelopmentManager,
  },
  {
    label: 'Approve Bills',
    path: '/bills/approval',
    keywords: 'expense approvals review',
    group: 'Pages',
    icon: HiCheckCircle,
    visible: canAccessBills,
  },
  {
    label: 'Bill Reconciliation',
    path: '/bills/reconciliation',
    keywords: 'reconcile match',
    group: 'Pages',
    icon: HiArrowsRightLeft,
    visible: canAccessBills,
  },

  // Admin
  {
    label: 'Bills Admin',
    path: '/bills/admin',
    keywords: 'manage expenses',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'Leave Admin',
    path: '/leave/admin',
    keywords: 'manage leave',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'Leave Settings',
    path: '/leave/settings',
    keywords: 'configure',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'Memo Admin',
    path: '/memo/admin',
    keywords: 'manage memos',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'Minutes Admin',
    path: '/minutes/admin',
    keywords: 'manage meetings',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'Cafe Admin',
    path: '/cafe/admin',
    keywords: 'manage cafe',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'Meeting Room Admin',
    path: '/meeting-room/admin',
    keywords: 'manage rooms',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'KPI Admin',
    path: '/kpi/admin',
    keywords: 'manage kpi',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'KPI Question Lists',
    path: '/kpi/questions',
    keywords: 'questions',
    group: 'Admin',
    icon: HiQuestionMarkCircle,
    visible: isSystemDev,
  },
  {
    label: 'KPI Submissions',
    path: '/kpi/submissions',
    keywords: 'review submissions',
    group: 'Admin',
    icon: HiInboxArrowDown,
    visible: isSystemDev,
  },
  {
    label: 'KPI Archive',
    path: '/kpi/archive',
    keywords: 'archived',
    group: 'Admin',
    icon: HiArchiveBox,
    visible: isSystemDev,
  },
  {
    label: 'Admin Dashboard',
    path: '/admin',
    keywords: 'team apps services core values',
    group: 'Admin',
    icon: HiCog6Tooth,
    visible: isSystemDev,
  },
  {
    label: 'System Development',
    path: '/system-development',
    keywords: 'system status health',
    group: 'Admin',
    icon: HiWrenchScrewdriver,
    visible: isSystemDev,
  },
  {
    label: 'Duo Services',
    path: '/duo-services',
    keywords: 'apps hosting ownership security audit',
    group: 'Admin',
    icon: HiWrenchScrewdriver,
    visible: (c) => c.duoServicesAccess,
  },
  {
    label: 'Wellbeing Allowance',
    path: '/wellbeing-allowance',
    keywords: 'health wellbeing offshore tier dependant allowance',
    group: 'Admin',
    icon: HiHeart,
    visible: (c) => c.wellbeingAllowanceAccess,
  },
]

export const getVisibleEntries = (ctx: PermissionContext): SearchEntry[] =>
  SEARCH_REGISTRY.filter((e) => e.visible(ctx))
